import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getApiUrl } from '../config/api.config';
import InvestorNetworkLayout from '../components/InvestorNetworkLayout';
import ChangePasswordModal from '../components/ChangePasswordModal';
import AlertDialog from '../components/AlertDialog';
import { investorNetworkTabPath } from '../lib/investorNetworkNav';

const InvestorNetworkInvestorDetail = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { tenantId, id } = useParams();
    const tenantPath = tenantId || user?.tenantIdentifier || 'default';

    const [loading, setLoading] = useState(true);
    const [investor, setInvestor] = useState(null);
    const [investments, setInvestments] = useState([]);
    const [error, setError] = useState('');
    const [showProfileMenu, setShowProfileMenu] = useState(false);
    const profileMenuRef = useRef(null);
    const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
    const [alert, setAlert] = useState({ isOpen: false, title: '', message: '', type: 'info' });

    const formatCurrency = (amount) => {
        if (amount == null) return '-';
        return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
    };

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            setError('');
            try {
                const headers = { 'Authorization': `Bearer ${localStorage.getItem('token')}` };
                const listRes = await fetch(getApiUrl('users/my-network/investors'), { headers });
                if (!listRes.ok) throw new Error('Failed to load investors');
                const list = await listRes.json();
                const found = (list || []).find((i) => String(i.id) === String(id));
                if (!found) {
                    setError('Investor not found in your network');
                    setInvestor(null);
                    setInvestments([]);
                    return;
                }
                setInvestor(found);

                const invRes = await fetch(getApiUrl(`investments/investor/${id}`), { headers });
                if (invRes.ok) {
                    const invList = await invRes.json();
                    setInvestments(Array.isArray(invList) ? invList : []);
                } else {
                    setInvestments([]);
                }
            } catch (e) {
                console.error('Failed to load investor detail:', e);
                setError('Failed to load investor details');
            } finally {
                setLoading(false);
            }
        };
        if (user?.id) load();
    }, [id, user?.id]);

    const stats = useMemo(() => {
        const total = (investments || []).reduce((sum, inv) => sum + (inv.investmentAmount || 0), 0);
        const startups = new Set((investments || []).map((inv) => inv.startupId)).size;
        return { total, startups, investments: (investments || []).length };
    }, [investments]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
                setShowProfileMenu(false);
            }
        };
        if (showProfileMenu) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showProfileMenu]);

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-4 border-indigo-600 border-t-transparent" />
            </div>
        );
    }

    return (
        <>
            <InvestorNetworkLayout
                activeTab="investors"
                tenantPath={tenantPath}
                onNotify={(n) => setAlert({ ...n, isOpen: true })}
                header={(
                    <header className="h-14 bg-white/80 backdrop-blur-sm border-b border-slate-200/80 flex items-center justify-between px-6 sticky top-0 z-30 gap-4">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                            <button
                                type="button"
                                onClick={() => navigate(investorNetworkTabPath(tenantPath, 'investors'))}
                                className="shrink-0 text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                            >
                                ← Back to Investors
                            </button>
                            <h1 className="text-xl font-bold text-gray-900 truncate">Investor Details</h1>
                        </div>
                        <div className="flex items-center shrink-0">
                            <div className="relative" ref={profileMenuRef}>
                                <button
                                    onClick={() => setShowProfileMenu(!showProfileMenu)}
                                    className="flex items-center space-x-2 p-2 hover:bg-gray-100 rounded-lg transition"
                                >
                                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                                        <span className="text-white text-sm font-bold">{user?.name?.charAt(0)?.toUpperCase()}</span>
                                    </div>
                                </button>
                                {showProfileMenu && (
                                    <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-lg border border-gray-100 py-1 z-50">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setShowChangePasswordModal(true);
                                                setShowProfileMenu(false);
                                            }}
                                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                        >
                                            Change password
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => navigate(`/${tenantPath}/settings`)}
                                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                        >
                                            Settings
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </header>
                )}
            >
                <div className="p-6 space-y-6">
                    {error ? (
                        <div className="bg-white rounded-2xl border border-red-200 p-6 text-red-700">{error}</div>
                    ) : (
                        <>
                            <div className="bg-white rounded-2xl border border-gray-200 p-6">
                                <div className="flex items-start gap-4">
                                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow">
                                        <span className="text-white font-bold text-lg">{investor?.name?.charAt(0)?.toUpperCase() || 'I'}</span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h2 className="text-2xl font-bold text-gray-900">{investor?.name}</h2>
                                        <p className="text-gray-500">{investor?.email}</p>
                                        {investor?.jobTitle && <p className="text-sm text-gray-500 mt-1">{investor.jobTitle}</p>}
                                        {investor?.phoneNumber && <p className="text-sm text-gray-500 mt-1">{investor.phoneNumber}</p>}
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <div className="bg-white rounded-xl border border-gray-200 p-4">
                                    <p className="text-xs text-gray-500 uppercase">Total invested</p>
                                    <p className="text-xl font-bold text-gray-900 mt-1">{formatCurrency(stats.total)}</p>
                                </div>
                                <div className="bg-white rounded-xl border border-gray-200 p-4">
                                    <p className="text-xs text-gray-500 uppercase">Investments</p>
                                    <p className="text-xl font-bold text-gray-900 mt-1">{stats.investments}</p>
                                </div>
                                <div className="bg-white rounded-xl border border-gray-200 p-4">
                                    <p className="text-xs text-gray-500 uppercase">Startups</p>
                                    <p className="text-xl font-bold text-gray-900 mt-1">{stats.startups}</p>
                                </div>
                            </div>

                            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                                <div className="px-5 py-4 border-b border-gray-100">
                                    <h2 className="text-lg font-semibold text-gray-900">Startup investments</h2>
                                </div>
                                {(investments || []).length === 0 ? (
                                    <div className="p-8 text-center text-gray-500">No investments yet for this investor.</div>
                                ) : (
                                    <div className="divide-y divide-gray-100">
                                        {(investments || []).map((inv) => {
                                            const company = (inv.startupCompanyName || '').trim();
                                            const founder = (inv.startupName || '').trim();
                                            const title = company || founder || 'Startup';
                                            const showFounder = Boolean(company && founder);
                                            return (
                                                <div key={inv.id} className="px-5 py-4 flex items-center justify-between gap-4">
                                                    <div className="min-w-0">
                                                        <p className="font-medium text-gray-900 truncate" title={title}>{title}</p>
                                                        {showFounder ? (
                                                            <p className="text-sm text-gray-600 truncate">Founder: {founder}</p>
                                                        ) : null}
                                                        <p className={`text-sm text-gray-500 ${showFounder ? 'mt-0.5' : ''}`}>
                                                            {inv.investmentStage || 'Round N/A'} · {inv.status || 'ACTIVE'}
                                                        </p>
                                                    </div>
                                                    <div className="flex items-center gap-3">
                                                        <p className="font-semibold text-gray-900">{formatCurrency(inv.investmentAmount)}</p>
                                                        <button
                                                            type="button"
                                                            onClick={() => navigate(`/${tenantPath}/investor-network/startups/${inv.startupId}?tab=investments`)}
                                                            className="px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg"
                                                        >
                                                            View startup
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </InvestorNetworkLayout>

            <AlertDialog
                isOpen={alert.isOpen}
                onClose={() => setAlert({ ...alert, isOpen: false })}
                title={alert.title}
                message={alert.message}
                type={alert.type}
            />
            <ChangePasswordModal
                isOpen={showChangePasswordModal}
                onClose={() => setShowChangePasswordModal(false)}
                onSuccess={() =>
                    setAlert({
                        isOpen: true,
                        title: 'Success',
                        message: 'Password updated successfully.',
                        type: 'success'
                    })
                }
            />
        </>
    );
};

export default InvestorNetworkInvestorDetail;
